import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import { CloseButton } from '@zillow/constellation';
import { Modal, Heading } from '@zillow/constellation-10';

import ModalFooter from '../../../Modal/ModalFooter';
import SpinnerContainer from '../../../SpinnerContainer';
import SecurityDepositUpdateFailureModal from '../../../Modal/SecurityDepositUpdateFailureModal';

import ReviewSecurityDepositConfirmationModal from '../../../rentGuarantee/TwoStepOptInReviewSecurityDeposit/components/ReviewSecurityDepositConfirmationModal';
import ReviewSecurityDepositMultiPolicyConfirmationModal from '../../../rentGuarantee/TwoStepOptInReviewSecurityDeposit/components/ReviewSecurityDepositMultiPolicyConfirmationModal';
import TwoStepOptInReviewSecurityDeposit from '../../../rentGuarantee/TwoStepOptInReviewSecurityDeposit';

import PropertiesActions from '../../../../actions/PropertiesActions';
import accessUtils from '../../../../utils/accessUtils';
import numberUtils from '../../../../utils/numberUtils';
import { useGetListingDetails } from '../../../../hooks/queries/properties';
import {
  REQUIRE_SECURITY_DEPOSIT,
  WAIVE_SECURITY_DEPOSIT,
} from '../../../rentGuarantee/TwoStepOptInReviewSecurityDeposit/constants';
import useReviewSecurityDeposit from '../../../../hooks/rentalProtection/useReviewSecurityDeposit';
import ManagePolicyCSEvents from '../../../../model/ClickstreamEvent/RentalProtection/ManagePolicyCSEvents';
import OptInCSEvents from '../../../../model/ClickstreamEvent/RentalProtection/OptInCSEvents';

const propTypes = {
  onClose: PropTypes.func.isRequired,
  listingAlias: PropTypes.string.isRequired,
  onSecurityDepositSubmitSuccess: PropTypes.func,
  onSubmit: PropTypes.func,
  isForManagePolicyPageV2: PropTypes.bool,
  inProgressModules: PropTypes.object,
};
const defaultProps = {
  onSecurityDepositSubmitSuccess: () => {},
  onSubmit: () => {},
  isForManagePolicyPageV2: false,
};

const ReviewSecurityDepositModal = ({
  onClose,
  listingAlias,
  onSecurityDepositSubmitSuccess,
  onSubmit,
  isForManagePolicyPageV2,
  inProgressModules,
}) => {
  const dispatch = useDispatch();
  const {
    properties: {
      currentProperty: {
        price: monthlyRentCurrentProperty,
        securityDeposit: securityDepositCurrentProperty,
        floorplans,
        syndication,
        rentalProtection: { isSecurityDepositWaived, isEligible: isRentalProtectionEligible },
      },
    },
  } = useSelector((state) => state);
  const user = useSelector((state) => state.user);
  const appConfig = useSelector((state) => state.app.appConfig);
  const { isLoading, data: propertyDetailsData } = useGetListingDetails(listingAlias);
  const canAccessLightweightOptin = accessUtils.canAccessRentalProtectionLightweightOptinExperiment({
    appConfig,
  });

  const getSecurityDepositFromListing = () => {
    if (securityDepositCurrentProperty) {
      return securityDepositCurrentProperty;
    }

    if (floorplans.length && floorplans[0].securityDeposit) {
      return floorplans[0].securityDeposit;
    }

    return null;
  };

  const getMonthlyRentFromListing = () => {
    if (monthlyRentCurrentProperty) {
      return monthlyRentCurrentProperty;
    }

    if (floorplans.length && floorplans[0].price) {
      return floorplans[0].price;
    }

    return null;
  };

  const monthlyRent = getMonthlyRentFromListing();
  const [securityDepositInputHasError, setSecurityDepositInputHasError] = useState(false);
  const [showSecurityDepositUpdateFailureModal, setShowSecurityDepositUpdateFailureModal] = useState(false);
  const [showSecurityDepositUpdatedConfirmationModal, setShowSecurityDepositUpdatedConfirmationModal] = useState(false);
  const [
    showSecurityDepositUpdatedMultiPolicyConfirmationModal,
    setShowSecurityDepositUpdatedMultiPolicyConfirmationModal,
  ] = useState(false);

  const { securityDepositSelection, securityDepositInputValue, setSecurityDepositOptions } = useReviewSecurityDeposit(
    null,
    getSecurityDepositFromListing(),
  );

  useEffect(() => {
    if (!isLoading) {
      const selectionType =
        isSecurityDepositWaived || securityDepositCurrentProperty === 0 ? WAIVE_SECURITY_DEPOSIT : null;
      setSecurityDepositOptions(selectionType, securityDepositCurrentProperty);
    }
  }, [isLoading]);

  useEffect(() => {
    if (monthlyRent && securityDepositInputValue) {
      const securityDepositLimit = monthlyRent * 1.5;

      if (securityDepositInputValue > securityDepositLimit) {
        setSecurityDepositInputHasError(true);
      } else {
        setSecurityDepositInputHasError(false);
      }
    }
  }, [securityDepositInputValue, monthlyRent]);

  const dispatchAnalytics = () => {
    if (isForManagePolicyPageV2) {
      ManagePolicyCSEvents.ModifySecurityDeposit({
        currentProperty: propertyDetailsData,
        user,
      });
    } else {
      OptInCSEvents.TWO_STEP_OPT_IN.ClickNextReviewSecurityDeposit({
        currentProperty: propertyDetailsData,
        user,
      });
    }
  };

  const handleMultiPolicySecurityDepositSubmit = () => {
    setShowSecurityDepositUpdatedMultiPolicyConfirmationModal(true);
  };

  const handleMultiPolicySecurityDepositCancel = () => {
    setShowSecurityDepositUpdatedMultiPolicyConfirmationModal(false);
  };

  const handleSecurityDepositSubmit = () => {
    dispatchAnalytics();
    let formattedUpdatedDeposit;
    if (securityDepositInputValue < 0.01 || securityDepositSelection === WAIVE_SECURITY_DEPOSIT) {
      setSecurityDepositOptions(securityDepositSelection, 0);
      formattedUpdatedDeposit = numberUtils.asCurrency(0, '0');
    } else {
      formattedUpdatedDeposit = numberUtils.asCurrency(securityDepositInputValue, '0');
    }
    const isListingSecurityDepositReviewedForLightweight =
      isForManagePolicyPageV2 && canAccessLightweightOptin && isRentalProtectionEligible;
    dispatch(
      PropertiesActions.updateOrWaiveSecurityDepositForListing(
        listingAlias,
        securityDepositSelection === WAIVE_SECURITY_DEPOSIT ? null : formattedUpdatedDeposit,
        securityDepositSelection === WAIVE_SECURITY_DEPOSIT,
        isListingSecurityDepositReviewedForLightweight,
      ),
    )
      .then(() => {
        setShowSecurityDepositUpdateFailureModal(false);

        if (inProgressModules.length > 1) {
          onSecurityDepositSubmitSuccess();
        } else {
          setShowSecurityDepositUpdatedConfirmationModal(true);
        }

        if (isForManagePolicyPageV2 && onSubmit) {
          onSubmit(numberUtils.asCurrency(formattedUpdatedDeposit, '$0,0'));
        }
      })
      .catch(() => {
        setShowSecurityDepositUpdateFailureModal(true);
      });
  };

  const closeSecurityDepositUpdateFailureModal = () => {
    setShowSecurityDepositUpdateFailureModal(false);
  };

  const closeSecurityDepositAfterSubmit = () => {
    onSecurityDepositSubmitSuccess();
    onClose();
  };

  if (showSecurityDepositUpdatedConfirmationModal) {
    return (
      <ReviewSecurityDepositConfirmationModal
        isForManagePolicyPageV2={isForManagePolicyPageV2}
        onClose={isForManagePolicyPageV2 ? closeSecurityDepositAfterSubmit : onClose}
        shouldCloseOnOutsideClick={false}
        securityDepositValue={securityDepositInputValue}
        syndication={syndication}
        securityDepositSelection={securityDepositSelection}
        onSecurityDepositSubmitSuccess={closeSecurityDepositAfterSubmit}
      />
    );
  }

  if (showSecurityDepositUpdatedMultiPolicyConfirmationModal) {
    return (
      <ReviewSecurityDepositMultiPolicyConfirmationModal
        onClose={onClose}
        onCancel={handleMultiPolicySecurityDepositCancel}
        onSubmit={handleSecurityDepositSubmit}
        inProgressModules={inProgressModules}
      />
    );
  }

  return (
    <Modal
      open={true}
      onOpenChange={(open) => !open && onClose()}
      header={<Heading level={3}>{isForManagePolicyPageV2 ? 'Security Deposit' : 'Request started'}</Heading>}
      footer={
        <ModalFooter
          submitText="Confirm"
          onCancel={isForManagePolicyPageV2 && onClose}
          onSubmit={
            inProgressModules?.length > 1 ? handleMultiPolicySecurityDepositSubmit : handleSecurityDepositSubmit
          }
          onSubmitDataEventId={
            isForManagePolicyPageV2 && inProgressModules?.length <= 1
              ? ManagePolicyCSEvents.ModifySecurityDeposit.data_event_id
              : null
          }
          hasError={showSecurityDepositUpdateFailureModal}
          isSubmitDisabled={
            isLoading ||
            securityDepositSelection === null ||
            (securityDepositSelection === REQUIRE_SECURITY_DEPOSIT &&
              !numberUtils.isOnlyIntegers(securityDepositInputValue)) ||
            (securityDepositInputHasError && securityDepositSelection === REQUIRE_SECURITY_DEPOSIT)
          }
        />
      }
      body={
        <div data-testid="rental-protection-two-step-opt-in-review-security-deposit-modal">
          {isForManagePolicyPageV2 && <CloseButton />}
          {isLoading ? (
            <SpinnerContainer />
          ) : (
            <>
              <TwoStepOptInReviewSecurityDeposit
                securityDepositSelection={securityDepositSelection}
                securityDepositInputValue={securityDepositInputValue}
                setSecurityDepositOptions={setSecurityDepositOptions}
                securityDepositInputHasError={securityDepositInputHasError}
                securityDepositCurrentProperty={securityDepositCurrentProperty}
                isForManagePolicyPageV2={isForManagePolicyPageV2}
              />
              {showSecurityDepositUpdateFailureModal && (
                <SecurityDepositUpdateFailureModal close={closeSecurityDepositUpdateFailureModal} />
              )}
            </>
          )}
        </div>
      }
    />
  );
};

ReviewSecurityDepositModal.propTypes = propTypes;
ReviewSecurityDepositModal.defaultProps = defaultProps;

export default ReviewSecurityDepositModal;
